// /tests — the worksheet/test designer's list (Module P2). Calm card list, like Classes/Dashboard:
// title, kind, status, total marks, last edited. "New test" / "New worksheet" create an empty draft
// and jump straight into the editor. Owner-only edit (the API enforces it); a Lead can switch to
// "Every teacher's" for oversight — still no student data, only the paper's own content.

import { api } from "../api";
import { esc } from "../dom";
import { navigate } from "../router";
import { me } from "../state";
import { loadItemBank, blockFromCard } from "../itembank";
import { pageHeader } from "../ui";
import { departmentGradeFromClass } from "../class-grade";
import { materialContextQuery, type MaterialWorksheet } from "../materials-engine";
import { loadWeeklyPreparation } from "../weekly-preparation";
import { termNumber } from "../curriculum";
import { resolveClassLesson, type ClassRow, type TermInfo } from "../lesson-data";
import '../weekly-worksheets.css';

interface SavedMaterial extends MaterialWorksheet { updated_at:string;activity_count:number;can_edit:boolean }

async function mountWorksheetStarter(host:HTMLElement,query:URLSearchParams):Promise<void> {
  host.innerHTML='<p role="status">Loading your classes and school weeks…</p>';
  try {
    const [classData,currentTerm,manifest]=await Promise.all([
      api<{classes:ClassRow[]}>('/api/classes?scope=own'),
      api<TermInfo>('/api/terms/current'),
      api<{documents:{node_id:string;title:string}[]}>('/api/material-documents'),
    ]);
    const classes=classData.classes;
    if(!classes.length){host.innerHTML='<p>No class is assigned to you yet. Ask the Lead to assign your class before making a weekly worksheet.</p>';return;}
    let cls=classes.find(c=>c.id===query.get('class'))??classes[0]!;
    let term=currentTerm;
    if(query.get('term')&&query.get('term')!==term.term.id){
      const selected=await loadWeeklyPreparation({classId:cls.id,termId:query.get('term')!,weekNo:Number(query.get('week'))||1});
      term={term:selected.term,current_week:selected.week_no,is_current:false,weeks:Array.from({length:selected.term.week_count},(_,i)=>i+1)};
    }
    const weeks=term.weeks??Array.from({length:term.term.week_count??0},(_,i)=>i+1);
    let week=weeks.includes(Number(query.get('week')))?Number(query.get('week')):term.current_week??weeks[0]??1;
    let sequence=0;
    host.innerHTML=`<div class="ww-pickers"><label>Class<select id="worksheet-class">${classes.map(c=>`<option value="${esc(c.id)}" ${c.id===cls.id?'selected':''}>${esc(c.name)}</option>`).join('')}</select></label><label>School week · ${esc(term.term.name)}<select id="worksheet-week">${weeks.map(w=>`<option value="${w}" ${w===week?'selected':''}>Week ${w}</option>`).join('')}</select></label></div><div id="worksheet-source" aria-live="polite"></div>`;
    async function update(){
      const at=++sequence;const target=host.querySelector<HTMLElement>('#worksheet-source')!;
      target.innerHTML='<p role="status">Finding this week’s activities…</p>';
      try {
        const weekly=await loadWeeklyPreparation({classId:cls.id,termId:term.term.id,weekNo:week});
        const result={lessons:weekly.lessons.filter(l=>l.can_open).map(l=>({lesson_id:l.id,node_id:l.node_id}))};
        // The picked school week's lesson in the year plan (owner ruling A2): the class's own plans do not choose it.
        const pickedTerm=termNumber(term.term);
        const resolved=await resolveClassLesson(cls,{...term,current_week:week},pickedTerm?{term:pickedTerm,week}:undefined);
        if(at!==sequence)return;
        const options=manifest.documents.filter(d=>d.node_id.startsWith(`G${departmentGradeFromClass(cls)}-`));
        const chosen=options.find(d=>d.node_id===query.get('node')&&query.get('class')===cls.id&&Number(query.get('week'))===week)??options.find(d=>d.node_id===resolved.node?.id);
        target.innerHTML=`<label>Activities for this week<select id="worksheet-node"><option value="">${options.length?'Choose an available activity week':'No worksheet activities available for this grade'}</option>${options.map(d=>`<option value="${esc(d.node_id)}" ${d===chosen?'selected':''}>${esc(d.title)} · ${esc(d.node_id)}</option>`).join('')}</select></label><p class="hint">Choose activities, preview the pupil copy, then print. This opens the worksheet maker; no empty paper is created.</p><button class="btn" id="open-weekly-worksheet" ${chosen?'':'disabled'}>Make a worksheet for this week</button>`;
        const select=target.querySelector<HTMLInputElement>('#worksheet-node')!;
        const button=target.querySelector<HTMLButtonElement>('#open-weekly-worksheet')!;
        select.addEventListener('change',()=>{button.disabled=!select.value;});
        button.addEventListener('click',()=>{
          const node=select.value;if(!node)return;
          const lessons=result.lessons.filter(l=>l.node_id===node);
          // Preserve an explicitly selected session; attach automatically only when unambiguous.
          const lesson=lessons.find(l=>l.lesson_id===query.get('lesson'))??(lessons.length===1?lessons[0]:undefined);
          const q=new URLSearchParams({class:cls.id,term:term.term.id,week:String(week)});if(lesson)q.set('lesson',lesson.lesson_id);
          navigate('/materials-studio/'+encodeURIComponent(node)+'?'+q.toString());
        });
      }catch(error){if(at===sequence){target.innerHTML=`<p role="alert">Couldn’t load this week: ${esc((error as Error).message)}</p><button id="worksheet-source-retry">Retry</button>`;target.querySelector('#worksheet-source-retry')!.addEventListener('click',()=>void update());}}
    }
    host.querySelector<HTMLInputElement>('#worksheet-class')!.addEventListener('change',e=>{cls=classes.find(c=>c.id===(e.target as HTMLSelectElement).value)!;void update();});
    host.querySelector<HTMLInputElement>('#worksheet-week')!.addEventListener('change',e=>{week=Number((e.target as HTMLSelectElement).value);void update();});
    await update();
  }catch(error){host.innerHTML=`<p role="alert">Couldn’t load worksheet choices: ${esc((error as Error).message)}</p><button id="worksheet-starter-retry">Retry choices</button>`;host.querySelector('#worksheet-starter-retry')!.addEventListener('click',()=>void mountWorksheetStarter(host,query));}
}

export interface AssessmentRow {
  id: string;
  kind: "test" | "worksheet";
  title: string;
  grade: number | null;
  test_date: string | null;
  subject: string;
  status: "draft" | "tos_ok" | "checked";
  violation_count: number;
  total_marks: number;
  block_count: number;
  updated_at: string;
  teacher_name?: string;
}

// Same rule as the editor's own badge (F-A, WEB-LIVE-CHECK 2026-09-04): "TOS OK" means zero
// validate() findings. status can still say tos_ok on the error-free rule alone (the Lead's
// sign-off is separate), so a row with warning-level notes gets the neutral "{n} notes" chip
// instead of overclaiming — never a second "TOS OK" that the panel underneath would contradict.
export function statusLabel(a: AssessmentRow): string {
  if (a.status === "checked") return "Checked ✓";
  if (a.status === "tos_ok" && a.violation_count === 0) return "TOS OK";
  if (a.violation_count > 0) return `${a.violation_count} note${a.violation_count === 1 ? "" : "s"}`;
  return "Draft";
}
export function statusClass(a: AssessmentRow): string {
  if (a.status === "checked") return "approved";
  if (a.status === "tos_ok" && a.violation_count === 0) return "submitted";
  return "";
}

function fmtWhen(iso: string): string {
  const d = new Date(iso.includes("T") ? iso : iso.replace(" ", "T") + "Z");
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export async function renderTests(app: HTMLElement, _params: Record<string, string>, query: URLSearchParams): Promise<void> {
  const isLead = me()?.auth_role === "lead";
  let scope: "mine" | "all" = "mine";
  let removing: string | null = null; // "tap again to confirm" — the roster/remove pattern
  let creationError: string | null = null;
  // Where "go back and try again" points after a failed create — the session it was for, or the
  // Week card as before. Set only on the failure path; never a claim that anything was saved.
  let backHref = "/this-week";

  // A "draft your paper" deep-link from the Week card / dashboard (P1b Job 3) — there is no
  // /tests/new route; this page reads the query, runs the SAME create the "+ New test" button
  // uses (now carrying the grade), and opens straight into the editor. No list ever shows.
  // JOB-W02: `?lesson=` makes the paper part of that lesson SESSION. class and node are then
  // deliberately NOT sent — the server derives both from the stored lesson, and a request that
  // contradicted it would be refused rather than silently corrected. `?source=` records which
  // session choice the paper came from (the only trace a citation-only choice can leave).
  const newKind = query.get("new");
  if (newKind === "test" || newKind === "worksheet") {
    const gradeParam = Number(query.get("grade"));
    const grade = Number.isInteger(gradeParam) && gradeParam >= 1 && gradeParam <= 12 ? gradeParam : undefined;
    const title = query.get("title")?.trim() || (newKind === "test" ? "Untitled test" : "Untitled worksheet");
    app.innerHTML = `<h1>Preparing your ${newKind === "test" ? "test" : "worksheet"}…</h1><p class="hint" role="status">Opening the editor.</p>`;
    try {
      const cardIds = [...new Set(query.getAll("card"))];
      if (cardIds.some((id) => !id.trim()) || cardIds.length > 100) throw new Error("This worksheet selection is invalid. Return to the Week card and choose the cards again.");
      const bank = cardIds.length ? await loadItemBank() : null;
      const selected = cardIds.map((id) => bank?.cards.find((card) => card.id === id));
      if (selected.some((card) => !card)) throw new Error("A selected item-bank card is no longer available. Return to the Week card to refresh your selection; no worksheet was created.");
      const blocks = selected.map((card) => blockFromCard(card!));
      const lessonId = query.get("lesson");
      const sourceKey = query.get("source");
      const { id } = await api<{ id: string }>("/api/assessments", {
        method: "POST",
        body: JSON.stringify({
          kind: newKind, title, grade, blocks,
          ...(lessonId
            ? {
                lesson_id: lessonId,
                ...(sourceKey
                  ? {
                      source_resource_key: sourceKey,
                      source_resource_kind: query.get("source_kind") ?? undefined,
                      source_resource_title: query.get("source_title") ?? undefined,
                    }
                  : {}),
              }
            : { class_id: query.get("class"), node_id: query.get("node") }),
        }),
      });
      // Consume the creation URL: browser Back must not run its POST again and create a duplicate.
      history.replaceState({...history.state}, "", "/tests");
      navigate(`/tests/${id}`);
      return;
    } catch (error) {
      creationError = (error as Error).message || "Couldn't prepare the worksheet. Please try again from the Week card.";
      // Consume the creation URL even on failure: browser Back must not re-run the POST.
      backHref = query.get("lesson") ? `/lesson/${encodeURIComponent(query.get("lesson")!)}` : "/this-week";
      history.replaceState({...history.state}, "", "/tests");
    }
  }

  const row = (a: AssessmentRow): string => {
    const who = scope === "all" && a.teacher_name ? ` · ${esc(a.teacher_name)}` : "";
    return `<div class="card test-row" data-id="${esc(a.id)}">
      <a class="test-row-main" data-link href="/tests/${esc(a.id)}">
        <div style="display:flex;align-items:baseline;justify-content:space-between;gap:10px">
          <strong>${esc(a.title)}</strong>
          <span class="chip ${statusClass(a)}">${statusLabel(a)}</span>
        </div>
        <div class="muted" style="font-size:var(--text-sm);margin-top:2px">
          ${a.kind === "test" ? "Test" : "Worksheet"}${a.grade ? ` · G${a.grade}` : ""} · ${esc(a.subject)}${who}
          · ${a.total_marks} mark${a.total_marks === 1 ? "" : "s"} · ${a.block_count} block${a.block_count === 1 ? "" : "s"}
          · edited ${fmtWhen(a.updated_at)}
        </div>
      </a>
      ${scope === "mine" ? `<div class="row-actions" style="margin-top:6px">
        <a href="#" class="linkbtn quiet" data-act="remove" data-id="${esc(a.id)}">${removing === a.id ? "Remove? Tap again" : "Remove"}</a>
      </div>` : ""}
    </div>`;
  };

  // Render the shell (header + controls) synchronously, BEFORE the list ever fetches — so a
  // failed/slow /api/assessments call leaves a visible, controllable page instead of silently
  // doing nothing over whatever page the user was on before (the router only shows its own calm
  // offline state for a NETWORK failure; a real HTTP error here has nowhere else to surface).
  function shell(): void {
    app.innerHTML = `
      ${pageHeader({ title: "Worksheets & tests", sub: "Build a worksheet or a test to print. Tests are checked against the department's Table of Specifications as you go." })}
      <section class="card ww-start"><h2>Make a worksheet for this week</h2><p>Choose your class and school week, then select the activities you want.</p><div id="worksheet-starter"></div></section>
      ${isLead ? `<div class="seg" style="max-width:280px">
          <button class="seg-btn ${scope === "mine" ? "on" : ""}" type="button" data-scope="mine">Mine</button>
          <button class="seg-btn ${scope === "all" ? "on" : ""}" type="button" data-scope="all">Every teacher's</button>
        </div>` : ""}
      <details class="card"><summary>Formal tests and papers written from scratch</summary><p>Formal tests use the department’s Table of Specifications and allocation checks. A blank practice paper lets you write your own questions.</p><div class="ww-pickers"><button class="btn secondary" id="new-test" type="button">Create a formal test</button><button class="btn secondary" id="new-worksheet" type="button">Write a blank practice paper</button></div></details>
      ${creationError ? `<p class="hint" role="alert">${esc(creationError)} <a class="linkbtn" data-link href="${esc(backHref)}">${backHref === "/this-week" ? "Back to the Week card" : "Back to the lesson"}</a></p>` : ""}
      <div id="err" class="error" hidden></div>
      <section aria-labelledby="saved-materials-title"><h2 id="saved-materials-title">Saved activity worksheets</h2><div id="material-list" role="status">Loading…</div></section>
      <section aria-labelledby="saved-assessments-title"><h2 id="saved-assessments-title">Saved tests and written papers</h2><div id="list" role="status">Loading…</div></section>`;
    wireControls();
    void mountWorksheetStarter(app.querySelector<HTMLElement>('#worksheet-starter')!,query);
  }

  function wireRows(): void {
    const err = app.querySelector<HTMLElement>("#err")!;
    app.querySelectorAll<HTMLAnchorElement>('[data-act="remove"]').forEach((a) => {
      a.addEventListener("click", async (e) => {
        e.preventDefault();
        const id = a.dataset.id!;
        if (removing !== id) {
          removing = id;
          void load();
          return;
        }
        try {
          await api(`/api/assessments/${encodeURIComponent(id)}`, { method: "DELETE" });
          removing = null;
          await load();
        } catch (ex) {
          err.textContent = (ex as Error).message;
          err.hidden = false;
        }
      });
    });
  }

  async function loadMaterials():Promise<void>{
    const target=app.querySelector<HTMLElement>('#material-list')!;const selectedScope=scope;
    target.innerHTML='<p role="status">Loading saved activity worksheets…</p>';
    try{const {worksheets}=await api<{worksheets:SavedMaterial[]}>(`/api/material-worksheets?scope=${selectedScope}`);
      if(app.querySelector('#material-list')!==target||selectedScope!==scope)return;
      target.innerHTML=worksheets.length?worksheets.map(w=>{const q=materialContextQuery(w);q.set('worksheet',w.id);return `<a class="card test-row test-row-main" data-link data-saved-material="${esc(w.id)}" href="/materials-studio/${encodeURIComponent(w.node_id)}?${esc(q.toString())}"><strong>${esc(w.title)}</strong><span>${esc(w.node_id)} · ${w.activity_count} activities · ${w.key_ready?'Teacher key ready':'Teacher key needs review'} · edited ${fmtWhen(w.updated_at)}</span></a>`;}).join(''):'<p class="empty">No saved activity worksheets yet.</p>';
    }catch(error){target.innerHTML=`<p role="alert">Couldn’t load activity worksheets: ${esc((error as Error).message)}</p><button id="retry-material-list">Retry activity worksheets</button>`;target.querySelector('#retry-material-list')!.addEventListener('click',()=>void loadMaterials());}
  }
  async function loadAssessments(): Promise<void> {
    const list = app.querySelector<HTMLElement>("#list")!;
    const err = app.querySelector<HTMLElement>("#err")!;
    err.hidden = true;
    try {
      const { assessments } = await api<{ assessments: AssessmentRow[] }>(`/api/assessments?scope=${scope}`);
      if (assessments.length) {
        list.className = "";
        list.innerHTML = assessments.map(row).join("");
      } else {
        list.className = "empty";
        list.textContent = scope === "mine" ? "No tests or worksheets yet — start one above." : "Nothing yet.";
      }
      wireRows();
    } catch (ex) {
      list.className = "empty";
      list.innerHTML = `<p role="alert">Couldn’t load tests and written papers: ${esc((ex as Error).message)}</p><button id="retry-assessment-list">Retry tests and written papers</button>`;
      list.querySelector('#retry-assessment-list')!.addEventListener('click',()=>void loadAssessments());
    }
  }

  async function load():Promise<void>{await Promise.all([loadMaterials(),loadAssessments()]);}

  function wireControls(): void {
    const err = app.querySelector<HTMLElement>("#err")!;
    const create = async (kind: "test" | "worksheet"): Promise<void> => {
      err.hidden = true;
      const buttons = app.querySelectorAll<HTMLButtonElement>("#new-test, #new-worksheet");
      if ([...buttons].some((button) => button.disabled)) return;
      buttons.forEach((button) => { button.disabled = true; });
      try {
        const title = kind === "test" ? "Untitled test" : "Untitled worksheet";
        const { id } = await api<{ id: string }>("/api/assessments", { method: "POST", body: JSON.stringify({ kind, title }) });
        navigate(`/tests/${id}`);
      } catch (ex) {
        err.textContent = (ex as Error).message;
        err.hidden = false;
        buttons.forEach((button) => { button.disabled = false; });
      }
    };
    app.querySelector("#new-test")?.addEventListener("click", () => void create("test"));
    app.querySelector("#new-worksheet")?.addEventListener("click", () => void create("worksheet"));

    app.querySelectorAll<HTMLButtonElement>("[data-scope]").forEach((b) => {
      b.addEventListener("click", () => {
        scope = b.dataset.scope as "mine" | "all";
        removing = null;
        shell();
        void load();
      });
    });
  }

  shell();
  await load();
}
